// @flow

import * as React from 'react';
import {useState} from 'react';
import classNames from 'classnames';
import copy from 'copy-to-clipboard';
import {Text} from 'sail/Text';
import {TooltipContainer} from 'sail/Tooltip';
import {useAccount, useLocation} from 'docs/data';
import {getLoginUrl} from 'docs/util';
import './KeyToken.scss';

import type {Account} from 'docs/data';

type KeyType = 'publishable' | 'secret';

type LoggedInOverlayProps = {|
  account: Account,
  type: KeyType,
|};

const LoggedInOverlay = ({account, type}: LoggedInOverlayProps) => {
  const viewKeysUrl = `${account.dashboardUrl}/account/apikeys`;
  return (
    <Text size={13}>
      This is your {type} test mode <a href="/docs/keys">API key</a>.{' '}
      <a href={viewKeysUrl} target="_blank">
        View your other keys
      </a>
      .
    </Text>
  );
};

type LoggedOutOverlayProps = {|
  account: Account,
  type: KeyType,
|};

const LoggedOutOverlay = ({account, type}: LoggedOutOverlayProps) => {
  const {location} = useLocation();
  const loginUrl = getLoginUrl({account, returnUrl: location.toString()});

  return (
    <Text size={13}>
      This is a <strong>sample</strong> {type} test mode{' '}
      <a href="/docs/keys">API key</a>. <a href={loginUrl}>Sign in</a> to see
      examples pre-filled with your test keys.
    </Text>
  );
};

type KeyTokenProps = {|
  children: React.Node,
  className?: string,
  type: KeyType,
  value: string,
|};

function middleTruncate(str) {
  if (typeof str === 'string') {
    if (str.length > 90) {
      const truncatedText = `${str.substr(0, 28)}...${str.substr(
        str.length - 20,
        str.length,
      )}`;
      return (
        <>
          <span className="KeyToken--truncated">{truncatedText}</span>
          <span className="KeyToken--shadowText">{str}</span>
        </>
      );
    }
  }
  return str;
}

export const KeyToken = ({children, className, type, value}: KeyTokenProps) => {
  const {account} = useAccount();

  const [isDisplayingCopied, setIsDisplayingCopied] = useState(false);

  const body = (
    <span
      className={classNames('KeyToken', className)}
      data-key-type={type}
      onClick={() => {
        copy(value);
        setIsDisplayingCopied(true);
        setTimeout(() => {
          setIsDisplayingCopied(false);
        }, 2000);
      }}
      role="button"
    >
      {middleTruncate(children)}
    </span>
  );

  let overlay;
  if (isDisplayingCopied) {
    overlay = <Text size={13}>Copied!</Text>;
  } else if (account.merchant?.isLoggedIn) {
    overlay = <LoggedInOverlay account={account} type={type} />;
  } else {
    overlay = <LoggedOutOverlay account={account} type={type} />;
  }

  return (
    <TooltipContainer
      allowMouseOverContents
      context={body}
      tooltipContents={overlay}
      hideDelay={250}
      style={{display: 'inline'}}
      contextBoxStyle={{display: 'inline'}}
    />
  );
};
